import { useEffect, useState } from 'react';
import { BookOpen, UserRound, Target } from 'lucide-react';
import { Barre, Puce, Riche, TEINTES, criteresDe, idsDe } from './constantes';
import { CritereGuide } from './CritereGuide';
import { ChecklistBloc, OutilsBloc } from './OutilsBloc';

const EnteteBloc = ({ bloc, teinte, progression }) => {
  const criteres = criteresDe(bloc);
  const { pret, total } = progression.compte(idsDe(criteres));
  return (
    <div className={`rounded-2xl border-t-4 bg-[#0F172A] p-6 text-white sm:p-8 ${teinte.bord}`} data-testid={`guide-bloc-entete-${bloc.code}`}>
      <p className={`text-xs font-bold uppercase tracking-[0.2em] ${teinte.texte}`}>Bloc {bloc.code.slice(-1)} · {bloc.court}</p>
      <h2 className="mt-3 max-w-4xl text-2xl font-bold leading-tight sm:text-3xl">{bloc.titre}</h2>
      <p className="mt-3 text-sm text-white/60">{bloc.activites.length} activités · {criteres.length} critères · {total} indicateurs</p>
      {bloc.minimum && <p className="mt-2 text-sm font-semibold text-amber-300" data-testid={`guide-bloc-minimum-${bloc.code}`}>Minimum fixé par l'arrêté : {bloc.minimum}</p>}
      <div className="mt-5 flex max-w-md items-center gap-3">
        <Barre pret={pret} total={total} couleur={teinte.barre} className="flex-1 bg-white/15" testId={`guide-bloc-barre-${bloc.code}`} />
        <span className="text-xs font-semibold text-white/70" data-testid={`guide-bloc-compte-${bloc.code}`}>{pret}/{total} prêts</span>
      </div>
    </div>
  );
};

const DeQuoi = ({ bloc, teinte }) => (
  <div className={`rounded-2xl border-2 bg-white p-5 sm:p-7 ${teinte.bord}`} data-testid={`guide-de-quoi-${bloc.code}`}>
    <h3 className={`flex items-center gap-2 font-bold ${teinte.texte}`}><BookOpen className="h-5 w-5" /> De quoi parle ce bloc ? (à lire avant de commencer)</h3>
    <div className="mt-3 space-y-2.5">{bloc.de_quoi.map((x) => <p key={x} className="text-sm leading-relaxed text-slate-700"><Riche texte={x} /></p>)}</div>
  </div>
);

const ProjetFictif = ({ p }) => (
  <div className="rounded-2xl border-l-4 border-[#E9A23B] bg-[#FFF8E6] p-5 sm:p-7" data-testid="guide-projet-fictif">
    <h3 className="flex items-center gap-2 font-bold text-[#7A4B00]"><Target className="h-5 w-5" /> Le projet fictif : « {p.nom} »</h3>
    <p className="mt-2 text-sm text-[#7A4B00]">{p.resume}</p>
    <div className="mt-4 grid gap-3 md:grid-cols-[3fr_2fr]">
      <div className="rounded-xl bg-white p-4" data-testid="guide-objectif-principal">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#7A4B00]">Objectif principal</p>
        <p className="mt-1 font-semibold text-[#0F172A]">{p.principal}</p>
      </div>
      <div className="rounded-xl bg-white p-4" data-testid="guide-objectifs-secondaires">
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#7A4B00]">Objectifs secondaires</p>
        <ul className="mt-1 space-y-1">{p.secondaires.map((x) => <Puce key={x}>{x}</Puce>)}</ul>
      </div>
    </div>
  </div>
);

const Presentation = ({ p, teinte }) => (
  <div className={`rounded-2xl p-5 sm:p-7 ${teinte.clair}`} data-testid="guide-presentation-candidat">
    <h3 className={`flex items-center gap-2 font-bold ${teinte.texte}`}><UserRound className="h-5 w-5" /> {p.titre}</h3>
    <p className="mt-2 text-sm text-slate-700">{p.texte}</p>
    <ul className="mt-3 space-y-1.5">{p.points.map((x) => <Puce key={x}>{x}</Puce>)}</ul>
    <p className="mt-4 rounded-xl border-l-4 border-[#E9A23B] bg-[#FFF8E6] p-3.5 text-sm italic text-[#7A4B00]"><b className="not-italic">Exemple : </b>{p.exemple}</p>
  </div>
);

const PlanBloc = ({ bloc, teinte, progression, onAller }) => (
  <div data-testid={`guide-plan-${bloc.code}`}>
    <h3 className="mb-3 text-base font-bold text-[#0F172A] md:text-lg">Plan du {bloc.code}</h3>
    <div className={`grid gap-3 sm:grid-cols-2 ${bloc.activites.length > 2 ? 'xl:grid-cols-4' : ''}`}>
      {bloc.activites.map((a) => (
        <div key={a.code} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className={`p-4 text-white ${teinte.fort}`}><p className="font-bold">{a.code} · {a.court}</p><p className="mt-1 text-xs leading-relaxed text-white/85" data-testid={`guide-plan-activite-${a.code}`}>{a.titre}</p></div>
          <ul className="space-y-1 p-2">
            {a.criteres.map((c) => {
              const { pret, total } = progression.compte(idsDe([c]));
              return (
                <li key={c.cle}>
                  <button type="button" onClick={() => onAller(c.cle)} data-testid={`guide-plan-critere-${c.cle}`}
                    className="flex w-full items-start gap-2 rounded-xl px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50">
                    <span className={`font-bold ${teinte.texte}`}>{c.code}</span><span className="min-w-0 flex-1 text-slate-700">{c.court}</span>
                    <span className={`text-xs font-semibold ${pret === total ? 'text-emerald-600' : 'text-slate-400'}`}>{pret}/{total}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  </div>
);

export const OngletBloc = ({ bloc, progression, cible }) => {
  const teinte = TEINTES[bloc.couleur];
  const [ouverts, setOuverts] = useState(() => [criteresDe(bloc)[0].cle]);
  const aller = (cle) => {
    setOuverts((o) => (o.includes(cle) ? o : [...o, cle]));
    setTimeout(() => document.getElementById(`critere-${cle}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };
  useEffect(() => { if (cible) aller(cible.cle); }, [cible]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid={`guide-bloc-${bloc.code}`}>
      <EnteteBloc bloc={bloc} teinte={teinte} progression={progression} />
      <DeQuoi bloc={bloc} teinte={teinte} />
      {bloc.projet_fictif && <ProjetFictif p={bloc.projet_fictif} />}
      {bloc.presentation && <Presentation p={bloc.presentation} teinte={teinte} />}
      <PlanBloc bloc={bloc} teinte={teinte} progression={progression} onAller={aller} />
      {bloc.activites.map((a) => (
        <div key={a.code} className="space-y-3">
          <p className={`text-xs font-bold uppercase tracking-[0.15em] ${teinte.texte}`}>{a.code} · {a.court}</p>
          {a.criteres.map((c) => (
            <CritereGuide key={c.cle} c={c} teinte={teinte} progression={progression} ouvert={ouverts.includes(c.cle)}
              onBasculer={() => setOuverts((o) => (o.includes(c.cle) ? o.filter((x) => x !== c.cle) : [...o, c.cle]))} />
          ))}
        </div>
      ))}
      <OutilsBloc bloc={bloc} teinte={teinte} />
      <ChecklistBloc bloc={bloc} teinte={teinte} progression={progression} />
    </div>
  );
};
